import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel } from '@headlessui/react';
import api from '../../utils/api';

const suggestedPlans = [
  { label: 'Cada mes', duration: 1 },
  { label: 'Trimestralmente', duration: 3 },
  { label: 'Cada 6 meses', duration: 6 },
  { label: 'Cada año', duration: 12 },
  { label: 'Cada 2 años', duration: 24 },
  { label: 'Cada 3 años', duration: 36 },
];

const EditServiceDrawer = ({ isOpen, service, onClose, onUpdated }) => {
  const [product, setProduct] = useState({
    category: '',
    type: service?.billing_type === 'one_time' ? 'single' : 'package',
    isActive: service?.status || 'inactive',
    isVisible: service?.is_visible === 1 ? 'active' : 'inactive',
    hideFromCategory: !!service?.hide_from_categories,
    name: service?.name || '',
    description: service?.description || '',
    price: service?.plans?.[0]?.price || '',
    plans: service?.plans?.filter(p => p.is_active === 1).map(p => ({
      duration_months: p.billing_interval,
      price: p.price,
    })) || [],
  });

  const [showPlanForm, setShowPlanForm] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [customPrice, setCustomPrice] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/categories');
        setCategories(response.data);
      } catch (err) {
        console.error('Error al cargar categorías:', err);
        setError('No se pudieron cargar las categorías');
      }
    };
    fetchCategories();

    // Update product state when service prop changes
    if (service) {
      setProduct({
        category: service.category || '',
        type: service.billing_type === 'one_time' ? 'single' : 'package',
        isActive: service.status || 'inactive',
        isVisible: service.is_visible === 1 ? 'active' : 'inactive',
        hideFromCategory: !!service.hide_from_categories,
        name: service.name || '',
        description: service.description || '',
        price: service.plans?.[0]?.price || '',
        plans: service.plans?.filter(p => p.is_active === 1).map(p => ({
          duration_months: p.billing_interval,
          price: p.price,
        })) || [],
      });
    }
  }, [service]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProduct((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 'active' : 'inactive') : value,
    }));
  };

  const handleTypeChange = (type) => {
    setProduct((prev) => ({ ...prev, type }));
  };

  const addPlanFromSuggestion = () => {
    if (!selectedSuggestion || !customPrice) return;
    setProduct((prev) => ({
      ...prev,
      plans: [...prev.plans, { duration_months: selectedSuggestion.duration, price: customPrice }],
    }));
    setSelectedSuggestion(null);
    setCustomPrice('');
    setShowPlanForm(false);
  };

  const removePlan = (index) => {
    setProduct((prev) => ({
      ...prev,
      plans: prev.plans.filter((_, i) => i !== index),
    }));
  };

  const handleUpdate = async () => {
    if (!product.name) {
      setError('El nombre del servicio es requerido');
      return;
    }

    if (product.type === 'single' && !product.price) {
      setError('El precio es requerido para productos únicos');
      return;
    }

    if (product.type === 'package' && product.plans.length === 0) {
      setError('Debe agregar al menos un plan para paquetes');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await api.put(`/api/services/${service.id}`, product);
      onUpdated?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al actualizar el servicio');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="fixed inset-y-0 right-0 max-w-full flex">
          <DialogPanel className="w-[800px] bg-white shadow-2xl rounded-l-lg flex flex-col h-screen">
            <div className="p-8 flex flex-col h-full">
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold text-gray-800">Editar producto</h1>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              <div className="flex-1 overflow-y-auto pr-4">
                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del servicio</label>
                  <input
                    type="text"
                    name="name"
                    value={product.name}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                  <textarea
                    name="description"
                    value={product.description}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                    rows="4"
                  ></textarea>
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categoría del producto</label>
                  <select
                    name="category"
                    value={product.category}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de producto</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      onClick={() => handleTypeChange('single')}
                      className={`relative border rounded-lg p-4 cursor-pointer transition ${
                        product.type === 'single' ? 'border-blue-800 bg-blue-50' : 'border-gray-300'
                      }`}
                    >
                      <h3 className="text-lg font-semibold text-gray-800">Producto único</h3>
                      <p className="text-sm text-gray-600">Un solo producto para una compra única.</p>
                      {product.type === 'single' && (
                        <svg className="absolute top-2 right-2 w-6 h-6 text-blue-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div
                      onClick={() => handleTypeChange('package')}
                      className={`relative border rounded-lg p-4 cursor-pointer transition ${
                        product.type === 'package' ? 'border-blue-800 bg-blue-50' : 'border-gray-300'
                      }`}
                    >
                      <h3 className="text-lg font-semibold text-gray-800">Paquete de productos</h3>
                      <p className="text-sm text-gray-600">Múltiples productos con facturación recurrente.</p>
                      {product.type === 'package' && (
                        <svg className="absolute top-2 right-2 w-6 h-6 text-blue-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-8 flex gap-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={product.isActive === 'active'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    ¿Está activo?
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isVisible"
                      checked={product.isVisible === 'active'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    ¿Es visible para los clientes?
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="hideFromCategory"
                      checked={product.hideFromCategory}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    ¿Ocultar de la vista de categorías?
                  </label>
                </div>

                {product.type === 'package' && (
                  <div className="mb-8">
                    <div className="bg-blue-50 border border-dashed border-blue-200 text-blue-900 text-sm rounded-lg px-4 py-6">
                      {product.plans.length === 0 ? (
                        <div className="text-center">
                          <p>Este servicio permite configurar múltiples ciclos de facturación.</p>
                          <button
                            onClick={() => setShowPlanForm(true)}
                            className="mt-4 px-4 py-2 border border-blue-700 text-blue-800 font-medium rounded hover:bg-blue-100"
                          >
                            Gestionar
                          </button>
                        </div>
                      ) : (
                        <div>
                          <h4 className="font-semibold text-sm mb-2">Planes configurados</h4>
                          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {product.plans.map((plan, i) => (
                              <li key={i} className="border rounded-lg p-4 bg-white flex justify-between items-center">
                                <div>
                                  <p className="text-sm font-medium text-gray-700">Cada {plan.duration_months} meses</p>
                                  <p className="text-gray-600 text-sm">${plan.price}</p>
                                </div>
                                <button onClick={() => removePlan(i)} className="text-red-500 hover:underline text-xs">Eliminar</button>
                              </li>
                            ))}
                          </ul>
                          <button
                            onClick={() => setShowPlanForm(true)}
                            className="mt-4 px-4 py-2 text-sm border border-blue-700 text-blue-800 font-medium rounded hover:bg-blue-100"
                          >
                            Añadir nuevo plazo
                          </button>
                        </div>
                      )}

                      {showPlanForm && (
                        <div className="mt-6 border rounded-lg bg-white p-4">
                          <h5 className="font-medium text-sm mb-2">Nuevo ciclo de facturación</h5>
                          <select
                            value={selectedSuggestion?.duration || ''}
                            onChange={(e) => {
                              const found = suggestedPlans.find((p) => p.duration.toString() === e.target.value);
                              setSelectedSuggestion(found || null);
                            }}
                            className="w-full border border-gray-300 rounded-lg p-2 mb-2"
                          >
                            <option value="">Seleccionar ciclo sugerido</option>
                            {suggestedPlans.map((p, idx) => (
                              <option key={idx} value={p.duration}>{p.label}</option>
                            ))}
                          </select>
                          {selectedSuggestion && (
                            <>
                              <label className="block text-sm font-medium text-gray-700">Precio</label>
                              <div className="flex items-center border border-gray-300 rounded-lg p-2 mb-2">
                                <img src="https://flagcdn.com/us.svg" alt="USD" className="w-5 h-5 mr-2" />
                                <span className="text-gray-700 mr-2">USD</span>
                                <input
                                  type="number"
                                  className="flex-1 border-none outline-none"
                                  value={customPrice}
                                  onChange={(e) => setCustomPrice(e.target.value)}
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                              <button
                                onClick={addPlanFromSuggestion}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                disabled={!customPrice}
                              >
                                Agregar
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {product.type === 'single' && (
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Precio</label>
                    <div className="flex items-center border border-gray-300 rounded-lg p-2">
                      <img src="https://flagcdn.com/us.svg" alt="USD" className="w-5 h-5 mr-2" />
                      <span className="text-gray-700 mr-2">USD</span>
                      <input
                        type="number"
                        name="price"
                        value={product.price}
                        onChange={handleChange}
                        className="flex-1 border-none outline-none"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 left-0 bg-white border-t pt-4 mt-4 flex justify-between">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cerrar
                </button>
                <button
                  onClick={handleUpdate}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-400"
                  disabled={isLoading}
                >
                  {isLoading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default EditServiceDrawer;
